import { createContext, useContext } from 'react';

import type { AuthenticatedUser, PermissionCode } from '@solar/contracts';

/**
 * Where the session is in its lifecycle.
 *
 * `bootstrapping` is distinct from `anonymous` because the difference matters
 * to the router: redirecting to the sign-in page while the refresh call is
 * still in flight would sign out every user who reloads the page.
 */
export type AuthStatus = 'bootstrapping' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-reads the identity, for use after a change to the caller's own roles. */
  reload: () => Promise<void>;
  /**
   * Whether the current user holds a permission.
   *
   * For deciding what to render. The server checks again on every request, so
   * a wrong answer here is a cosmetic bug, never a security hole.
   */
  can: (permission: PermissionCode) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>.');
  }

  return value;
}
